import React, { createContext, useState, useEffect, useContext } from 'react';
import { login as apiLogin, getMe } from '../api/auth';
import { CircularProgress, Box } from '@mui/material';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('ACCESS_TOKEN'));
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const validateToken = async () => {
            if (token) {
                try {
                    const response = await getMe();
                    setUser(response.data);
                } catch (error) {
                    console.error("Session token is invalid or expired.", error);
                    localStorage.removeItem('ACCESS_TOKEN');
                    setToken(null);
                    setUser(null);
                }
            }
            setLoading(false);
        };
        validateToken();
    }, [token]);

    const login = async (credentials) => {
        const response = await apiLogin(credentials);
        const { accessToken, refreshToken, email, displayName, role } = response.data;
        localStorage.setItem('ACCESS_TOKEN', accessToken);
        localStorage.setItem('REFRESH_TOKEN', refreshToken);
        setToken(accessToken);
        setUser({
            email,
            displayName,
            role
        });
        return response;
    };

    const logout = () => {
        localStorage.removeItem('ACCESS_TOKEN');
        localStorage.removeItem('REFRESH_TOKEN');
        setToken(null);
        setUser(null);
        // Optional: Call a /api/auth/logout endpoint if you have one
    };

    // Show a loading spinner while validating the initial token
    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '100vh'
            }}>
                <CircularProgress />
            </Box>
        );
    }

    const value = {
        user,
        setUser,
        token,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Export the useAuth hook directly from this file
export const useAuth = () => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }

    return context;
};

// Also export the AuthContext itself (though it's not typically needed)
export { AuthContext };
